import styled, { createGlobalStyle, css } from 'styled-components';
import { Textarea } from 'react-rainbow-components';
import { UniversalForm } from '@rainbow-modules/forms';
import Drawer from 'components/Drawer';
import Input from 'components/Input';
import Picklist from 'components/Picklist';
import DatePicker from 'components/DatePicker';
import CustomerPicker from 'components/CustomerPicker';
import Table from 'components/Table';
import {
    DIVIDER, GREY_300, GREY_500, GREY_800, RED_500, WHITE,
} from '../../../constants';

export const StyledDrawer = styled(Drawer)`
    width: min(72rem, calc(100vw - 3rem));

    div[id^='drawer-content'] {
        padding: 0;
    }
`;

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-sizing: border-box;
    overflow: hidden;
    height: 100%;
`;

export const Content = styled.div<{ $hidden?: boolean }>`
    display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
    padding: 0 1.5rem 1.5rem;
    box-sizing: border-box;
    flex-direction: column;
    overflow: auto;
    flex: 1;
    min-height: 0;
    position: relative;
`;

export const PreviewPane = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    padding: 1rem;
`;

export const StyledUniversalForm = styled(UniversalForm)`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
`;

export const FormContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

export const FieldsSection = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    gap: 1.5rem;
    padding: 1.5rem 0;

    div[id^='error-message'] {
        align-self: flex-start;
    }
`;

export const FormSection = styled.section`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

export const FieldSectionTitle = styled.h2`
    margin: 0 0 0.625rem;
    font-size: 0.9375rem;
    font-weight: 500;
    line-height: 1.3125rem;
    color: ${GREY_800};
`;

export const FormRow = styled.div<{ $columns?: number }>`
    display: grid;
    grid-template-columns: repeat(${({ $columns }) => $columns || 2}, minmax(0, 1fr));
    gap: 1rem;

    @media (max-width: 40rem) {
        grid-template-columns: 1fr;
    }
`;

export const BillToFieldsRow = styled(FormRow)`
    margin-top: 0.5rem;
`;

export const FeeList = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${DIVIDER};
    border-radius: 0.5rem;
    background: ${WHITE};
`;

export const FeeRow = styled.div<{ $total?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.625rem 1rem;
    font-weight: ${({ $total }) => ($total ? 700 : 400)};

    & + & {
        border-top: 1px solid ${DIVIDER};
    }
`;

export const FeeLabel = styled.span`
    color: ${GREY_800};
    font-size: 0.875rem;
`;

export const FeeAmount = styled.span`
    color: ${GREY_800};
    font-size: 0.875rem;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

export const NoteFields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

export const Footer = styled.footer`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 1.25rem 1.5rem 1.5rem;
`;

export const StyledTextarea = styled(Textarea)`
    display: flex;
    box-sizing: border-box;

    label {
        margin-left: 0;
        font-size: 0.8125rem;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    > div {
        border: none;
        box-shadow: none;
        box-sizing: border-box;
    }

    textarea {
        width: 100%;
        box-sizing: border-box;
        border-radius: 0.375rem;
        border: 1px solid ${DIVIDER};
        min-height: 5.5rem;
        padding: 0.5rem 0.875rem;

        :focus,
        :active {
            width: 100%;
            padding: 0.5rem 0.875rem;
            border: 1px solid ${DIVIDER};
        }

        ::placeholder {
            font-weight: 300 !important;
        }
    }
`;

export const StyledInput = styled(Input)`
    label {
        margin-left: 0;
        font-size: 0.8125rem;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    input {
        border-radius: 0.375rem;
        ${({ error }) => !error && css`
            border: 1px solid ${DIVIDER};
        `}
    }

    div[id^='error-message'] {
        align-self: flex-start;
    }
`;

export const StyledPicklist = styled(Picklist)`
    label {
        margin-left: 0;
        font-size: 0.8125rem;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    button,
    input {
        border-radius: 0.375rem;
        ${({ error }) => !error && css`
            border: 1px solid ${DIVIDER};
        `}
    }

    div[id^='error-message'] {
        align-self: flex-start;
    }
`;

export const StyledDatePicker = styled(DatePicker)`
    label {
        margin-left: 0;
        font-size: 0.8125rem;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }
`;

export const TemplateEmpty = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 12rem;
    padding: 1.5rem;
    box-sizing: border-box;
    font-size: 0.875rem;
    line-height: 1.375rem;
    text-align: center;
    color: ${GREY_300};
`;

export const StyledCustomerPicker = styled(CustomerPicker)`
    flex: 1;

    label {
        margin-left: 0;
        font-size: 0.8125rem;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
    }

    div[id^='error-message'] {
        align-self: flex-start;
    }
`;

export const StatusPicklistGlobalStyle = createGlobalStyle`
    div[id^='listbox-'][role='listbox'] {
        li > div#draft,
        li > div#sent,
        li > div#paid {
            white-space: unset;
            padding: 0.5rem 1.25rem;
        }
    }
`;

export const StatusOptionContainer = styled.div`
    display: flex;
    align-content: flex-start;
    justify-content: stretch;
    gap: 0.75rem;
`;

export const StatusOptionCopy = styled.div`
    display: flex;
    flex-direction: column;
`;

export const StatusOptionLabel = styled.span`
    font-size: 0.9375rem;
    font-weight: 400;
    line-height: 1.375rem;
    color: ${GREY_800};
`;

export const StatusOptionDescription = styled.span`
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1.25rem;
    color: ${GREY_300};
`;

export const LineItemsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

export const StyledLineTable = styled(Table)`
    height: auto;

    > div,
    > div > div {
        height: auto;
    }

    table {
        padding-bottom: 0;
    }

    th:first-child,
    td:first-child {
        padding-left: 0;

        > div {
            padding-left: 0;
        }
    }

    th:first-of-type .rainbow-table_header-container {
        padding-left: 4px;
    }
`;

export const LineCellWrapper = styled.div<{ $alignment?: string }>`
    display: flex;
    align-items: center;
    justify-content: ${({ $alignment }) => {
        if ($alignment === 'right') return 'flex-end';
        if ($alignment === 'center') return 'center';
        return 'flex-start';
    }};
    width: 100%;
    height: 100%;
    padding: 0.75rem 1rem;
    box-sizing: border-box;
    color: ${GREY_500};
    font-size: 0.9375rem;
    font-weight: 300;

    th:first-child &,
    td:first-child & {
        padding-left: 0;
    }

    > div {
        width: ${({ $alignment }) => ($alignment === 'right' || $alignment === 'center' ? 'auto' : '100%')};
        min-width: 0;
        flex: ${({ $alignment }) => ($alignment === 'right' || $alignment === 'center' ? '0 1 auto' : '1')};
    }

    div[id^='error-message'] {
        display: none;
    }
`;

export const LineActionsCell = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 0.5rem;
`;

export const LineItemsError = styled.div`
    padding: 0 1rem;
    font-size: 0.75rem;
    color: ${RED_500};
`;

export const AddLineButton = styled.div`
    display: flex;
    justify-content: flex-start;
`;
